import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";

import { parseBackTarget } from "@/app/back-target";

export type BackLinkProps = {
  /** 戻り先が URL に無い・信用できないときの既定（従来の固定リンク）。 */
  readonly fallback: {
    readonly to: string;
    /** 解決済みの文字列を渡す（i18n キーを引き回さない）。 */
    readonly label: string;
  };
};

/**
 * 「戻る」リンク。宛先は URL の `back` から決める。
 *
 * 同じ画面が複数の入口から開かれるため、戻り先を画面側で固定にすると遷移元と
 * 食い違う（承認の判定画面から案件詳細を開いてマイ案件へ飛ばされる、など）。
 */
export function BackLink({ fallback }: BackLinkProps) {
  const { t } = useTranslation(["common", "nav"]);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const target = parseBackTarget(searchParams);
  // 自分自身へ戻るリンクは出さない（手で URL に書かれた場合の保険）
  const isSelf =
    target !== null && target.to.split("?")[0] === location.pathname;
  const resolved = isSelf ? null : target;

  const name =
    resolved === null
      ? null
      : resolved.label.kind === "nav"
        ? t(`nav:items.${resolved.label.key}` as "nav:items.home")
        : t(
            `common:backTargets.${resolved.label.key}` as "common:backTargets.approvalReview",
          );

  return (
    <Link
      to={resolved?.to ?? fallback.to}
      // 置き場所はどれも flex-col の header。既定の stretch だと当たり判定が
      // 行いっぱいに広がるので文字幅に収める
      className="text-muted-foreground hover:text-foreground w-fit text-sm"
    >
      {name === null ? fallback.label : t("common:backTo", { name })}
    </Link>
  );
}
